import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/Form/Form';
import { PasswordInput } from '@/components/ui/PasswordInput/PasswordInput';
import { toast } from '@/components/ui/Toast/Toast';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { hasNoLeadingOrTrailingSpaces } from '@/utils/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { confirmResetPassword } from 'aws-amplify/auth';
import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PasswordFormField } from '../PasswordFormField/PasswordFormField';
import { validatePassword } from '../PasswordFormField/validation';

interface AuthNewPasswordFormProps {
  goToNextStep: () => void;
  goToPreviousStep: (errorMessage?: string) => void;
  username: string;
  code: string;
  persistedPassword?: string;
  persistPassword: (password: string) => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const AuthNewPasswordForm = ({
  goToNextStep,
  goToPreviousStep,
  username,
  code,
  persistedPassword,
  persistPassword,
  setIsLoading,
}: AuthNewPasswordFormProps) => {
  const { t } = useTranslation('auth', { keyPrefix: 'forgotPassword.steps.passwordReset' });
  const { t: tValidation } = useTranslation('utils', { keyPrefix: 'validation' });
  const { t: tError } = useTranslation('utils', { keyPrefix: 'error' });
  const { translateError } = useErrorTranslation();

  const formSchema = z
    .object({
      password: z
        .string()
        .refine((val) => validatePassword(val).isValid, {
          message: tValidation('passwordValidation'),
        })
        .refine((val) => hasNoLeadingOrTrailingSpaces(val), {
          message: tValidation('noLeadingAndTrailingSpaces'),
        }),
      repeatPassword: z.string().min(1, {
        message: tValidation('required'),
      }),
    })
    .superRefine((data, ctx) => {
      if (data.password !== data.repeatPassword) {
        ctx.addIssue({
          code: 'custom',
          message: tValidation('passwordMismatch'),
          path: ['repeatPassword'],
        });
      }
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: persistedPassword ?? '',
      repeatPassword: persistedPassword ?? '',
    },
  });

  const password = form.watch('password');
  const repeatPassword = form.watch('repeatPassword');

  useEffect(() => {
    if (form.formState.submitCount > 0) {
      void form.trigger('repeatPassword');
    }
  }, [password, repeatPassword, form]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);

    try {
      await confirmResetPassword({
        username: username.toLowerCase(),
        newPassword: values.password,
        confirmationCode: code,
      });
      goToNextStep();
    } catch (error: unknown) {
      if (error instanceof Error) {
        const errorMessage = translateError(error, t(`cognitoErrors.${error.name}` as never, tError('GENERAL_ERROR')));

        switch (error.name) {
          case 'CodeMismatchException':
          case 'ExpiredCodeException': {
            persistPassword(values.password);
            goToPreviousStep(errorMessage);
            break;
          }

          default: {
            toast({
              variant: 'destructive',
              description: errorMessage,
            });
            break;
          }
        }

        return;
      }

      toast({
        variant: 'destructive',
        description: translateError(error),
      });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Form {...form}>
      <form
        id='forgot-password-password-reset-form'
        onSubmit={(e) => {
          void form.handleSubmit(onSubmit)(e);
        }}
        className='flex flex-col gap-4'
      >
        <input type='hidden' id='username' name='username' value={username?.toLowerCase()} autoComplete='username' />
        <PasswordFormField control={form.control} name='password' autoComplete='new-password' />
        <FormField
          control={form.control}
          name='repeatPassword'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('repeatPassword.label')}</FormLabel>
              <FormControl>
                <PasswordInput {...field} autoComplete='new-password' aria-required />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};
